import type {
  MapFrameCamera,
  NorthArrowElement,
  NorthArrowOrientation,
  NorthArrowSymbol,
} from './layoutTypes';

/** Rotation inputs; the symbol is optional so callers can pass partials. */
export type NorthArrowRotationInput = Pick<
  NorthArrowElement,
  'orientation' | 'calibrationAngle'
> & {
  readonly symbol?: Pick<NorthArrowSymbol, 'angle' | 'angleAlignment'>;
};

const NORTH_MAGNETIC_POLE_2025 = {
  lon: 164,
  lat: 86.5,
} as const;

function toRad(value: number): number {
  return (value * Math.PI) / 180;
}

function toDeg(value: number): number {
  return (value * 180) / Math.PI;
}

export function normalizeSignedDegrees(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const normalized = ((value + 180) % 360 + 360) % 360 - 180;
  return Object.is(normalized, -0) ? 0 : normalized;
}

/**
 * Lightweight magnetic-north estimate based on the 2025 magnetic-pole
 * position. This is intentionally not labelled WMM: full World Magnetic Model
 * evaluation needs epoch coefficients and secular-variation updates.
 */
export function estimatedMagneticDeclinationDeg(
  lon: number,
  lat: number,
): number {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return 0;
  const phi1 = toRad(Math.max(-89.999, Math.min(89.999, lat)));
  const phi2 = toRad(NORTH_MAGNETIC_POLE_2025.lat);
  const deltaLon = toRad(NORTH_MAGNETIC_POLE_2025.lon - lon);
  const bearing = toDeg(
    Math.atan2(
      Math.sin(deltaLon) * Math.cos(phi2),
      Math.cos(phi1) * Math.sin(phi2)
        - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLon),
    ),
  );
  return normalizeSignedDegrees(bearing);
}

/**
 * Orientation correction relative to the map frame's visual north.
 * GeoViewer's page frame is Web Mercator: meridians are vertical, therefore
 * true- and grid-north convergence is zero in the current engine.
 */
export function orientationCorrectionDeg(
  orientation: NorthArrowOrientation,
  center: readonly [number, number],
): number {
  if (orientation === 'magnetic') {
    return estimatedMagneticDeclinationDeg(center[0], center[1]);
  }
  return 0;
}

/**
 * CSS rotation. Map bearing is clockwise; ArcGIS calibration is positive
 * counterclockwise, hence the opposite signs. `display` angle alignment pins
 * the symbol to the page, so the frame bearing drops out.
 */
export function northArrowRotationForElement(
  arrow: NorthArrowRotationInput,
  camera: MapFrameCamera,
): number {
  const followsMap = (arrow.symbol?.angleAlignment ?? 'map') === 'map';
  return normalizeSignedDegrees(
    (followsMap ? -camera.bearing : 0)
      + orientationCorrectionDeg(arrow.orientation, camera.center)
      - arrow.calibrationAngle
      + (arrow.symbol?.angle ?? 0),
  );
}

/** The angle ArcGIS reports as "Calculated angle" for the element. */
export function northArrowCalculatedAngle(
  arrow: NorthArrowRotationInput,
  camera: MapFrameCamera,
): number {
  return normalizeSignedDegrees(
    orientationCorrectionDeg(arrow.orientation, camera.center)
      - arrow.calibrationAngle,
  );
}

export function northArrowVisibleAtScale(
  arrow: Pick<
    NorthArrowElement,
    'visible' | 'maximumScaleDenominator' | 'minimumScaleDenominator'
  >,
  scaleDenominator: number | null,
): boolean {
  if (!arrow.visible) return false;
  if (scaleDenominator == null) return true;
  if (
    arrow.maximumScaleDenominator != null
    && scaleDenominator < arrow.maximumScaleDenominator
  ) return false;
  if (
    arrow.minimumScaleDenominator != null
    && scaleDenominator > arrow.minimumScaleDenominator
  ) return false;
  return true;
}
